import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import apiClient from '../api/client';
import { useAuthStore } from '../stores';
import { Loader } from '../components/Common/Loader';

/**
 * Handles WEBULL_REDIRECT_URI: /webull/callback
 * Logs every query param Webull sends back, exchanges the code on the server,
 * and prints the full token + account payloads to the console.
 */
const WebullCallbackPage: React.FC = () => {
  const { user, token } = useAuthStore();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'done' | 'error'>('loading');
  const [payload, setPayload] = useState<unknown>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      const search = window.location.search || '';
      const params = Object.fromEntries(new URLSearchParams(search).entries());

      console.log('[Webull OAuth] redirect query string', search);
      console.log('[Webull OAuth] redirect params (everything Webull sent back)', params);

      if (!token || !user) {
        setStatus('error');
        setError('You must be logged in as a master to complete Webull connect.');
        return;
      }

      if (user.role !== 'master') {
        setStatus('error');
        setError('Webull Connect is only available for master traders.');
        return;
      }

      if (!params.code) {
        setStatus('error');
        setError('No authorization code in the callback URL.');
        setPayload(params);
        return;
      }

      const expectedState = sessionStorage.getItem('webull_oauth_state');
      if (expectedState && params.state && expectedState !== params.state) {
        console.warn('[Webull OAuth] state mismatch', { expectedState, got: params.state });
      }

      try {
        const res = await apiClient.post('/brokerage/webull-oauth/callback', params);
        if (cancelled) return;
        console.log('[Webull OAuth] full server result (token + accounts + raw callback)', res.data);
        setPayload(res.data);
        setStatus('done');
        sessionStorage.removeItem('webull_oauth_state');
      } catch (err: any) {
        if (cancelled) return;
        const data = err?.response?.data || { error: err?.message || 'Callback failed' };
        console.error('[Webull OAuth] callback exchange failed', data);
        setPayload(data);
        setError(data?.error || data?.message || 'Failed to exchange Webull code');
        setStatus('error');
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [token, user]);

  if (status === 'loading') {
    return <Loader fullScreen message="Connecting Webull…" />;
  }

  return (
    <div className="min-h-screen bg-background text-foreground p-6 md:p-10">
      <div className="mx-auto max-w-4xl space-y-4">
        <h1 className="text-2xl font-semibold">
          {status === 'done' ? 'Webull connected' : 'Webull connect failed'}
        </h1>
        {error && <p className="text-sm text-destructive">{error}</p>}
        <p className="text-sm text-muted-foreground">
          Full response is in the browser console and server logs.
        </p>
        <pre className="max-h-[70vh] overflow-auto rounded-xl border border-border bg-card p-4 text-xs whitespace-pre-wrap break-all">
          {JSON.stringify(payload, null, 2)}
        </pre>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => navigate('/dashboard/webull')}
            className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground"
          >
            Back to Webull tab
          </button>
          <Link to="/dashboard/overview" className="rounded-xl border border-border px-4 py-2 text-sm">
            Dashboard
          </Link>
        </div>
      </div>
    </div>
  );
};

export default WebullCallbackPage;
